import type {
  JobImprovedGeneratedByPreview,
  JobImprovedGetDetailOutput,
  JobImprovedNestedInput,
  LocationInput,
} from 'clients/ReachoutClient/ReachoutClient.types';
import type { JobFormValues } from '../components/EditEnrichedVacancyForm/EditEnrichedVacancy.types';
import {
  DRIVER_LICENSE_CATEGORIES,
  EDUCATION_EQUIVALENTS,
  EDUCATION_LEVELS,
  EmploymentType,
  employmentTypeOptions,
  LANGUAGE_LEVELS,
  languageOptions,
  shiftOptions,
  workdayOptions,
  workdayPatterns,
  WorkdayType,
} from './EnrichVacancyModal.constants';

const isUnknownOrMissing = (value: string | undefined | null): boolean => {
  if (!value) return true;
  if (typeof value !== 'string') return false;
  return (
    value?.toLowerCase() === 'unknown' ||
    value?.toLowerCase() === 'missing' ||
    value?.toLowerCase() === 'none' ||
    value?.toLowerCase() === 'n/a'
  );
};

const parseNullableCoordinate = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const roundLatOrLngToSixDecimals = (coordinate: number): number => Number(coordinate.toFixed(6));

export const isLocationResolved = (
  employment:
    | {
        address?: string | null;
        latitude?: number | null;
        longitude?: number | null;
      }
    | undefined,
): boolean => {
  if (!employment) return false;
  const lat = parseNullableCoordinate(employment.latitude);
  const lng = parseNullableCoordinate(employment.longitude);
  return Boolean(employment.address?.trim()) && lat !== null && lng !== null;
};

const normalizeEmploymentType = (types: string | string[] | undefined | null): EmploymentType[] => {
  if (!types) return [];

  const typeArray = Array.isArray(types) ? types : types.split(',').map((t) => t.trim());

  return typeArray
    .map((type) => {
      if (!type) return null;

      const normalized = type.toLowerCase().replace(/[^a-z0-9]/g, '');

      const matchedType = employmentTypeOptions.find(
        (option) =>
          option.variations.en.some((variation) => normalized.includes(variation)) ||
          option.variations.nl.some((variation) => normalized.includes(variation)),
      )?.value;

      return matchedType || null;
    })
    .filter((type): type is EmploymentType => type !== null);
};

const normalizeWorkdays = (days: string | string[] | undefined | null): WorkdayType[] => {
  if (!days) return [];

  const dayArray = Array.isArray(days) ? days : days.split(',').map((d) => d.trim());
  const normalizedDays = new Set<WorkdayType>();

  dayArray.forEach((day) => {
    if (!day) return;

    const normalized = day
      .toLowerCase()
      .replace(/[^a-z0-9-\/ ]/g, '') // keep letters, numbers, hyphen, slash, and space
      .replace(/\s+/g, ' '); // replace multiple spaces with single space

    const matchedPattern = workdayPatterns.find(
      (pattern) =>
        pattern.variations.en.some((variation) => normalized.includes(variation)) ||
        pattern.variations.nl.some((variation) => normalized.includes(variation)),
    );

    if (matchedPattern) {
      matchedPattern.days.forEach((day) => normalizedDays.add(day));
      return;
    }

    const matchedDay = workdayOptions.find(
      (option) =>
        option.variations.en.some((variation) => normalized.includes(variation)) ||
        option.variations.nl.some((variation) => normalized.includes(variation)),
    )?.value;

    if (matchedDay) {
      normalizedDays.add(matchedDay);
    }
  });

  return Array.from(normalizedDays);
};

const normalizeLanguage = (language: string | string[] | undefined | null): string[] => {
  if (!language) return [];

  const languageArray = Array.isArray(language) ? language : language.split(',').map((l) => l.trim());
  const normalizedLanguages = new Set<string>();

  languageArray.forEach((lang) => {
    if (!lang) return;

    const normalized = lang
      .toLowerCase()
      .replace(/[^a-z0-9-\/ ]/g, '')
      .replace(/\s+/g, ' ');

    const matchedLanguage = languageOptions.find((option) => option.value === normalized.toUpperCase())?.value;
    if (matchedLanguage) {
      normalizedLanguages.add(matchedLanguage);
      return;
    }

    const matchedLabel = languageOptions.find(
      (option) =>
        option.label.en.toLowerCase().replace(/[^a-z0-9]/g, '') === normalized ||
        option.label.nl.toLowerCase().replace(/[^a-z0-9]/g, '') === normalized,
    )?.value;

    if (matchedLabel) {
      normalizedLanguages.add(matchedLabel);
    }
  });

  return Array.from(normalizedLanguages);
};

const normalizeShifts = (shifts: string | string[] | undefined | null): string[] => {
  if (!shifts) return [];

  const shiftArray = Array.isArray(shifts) ? shifts : shifts.split(',').map((s) => s.trim());

  return shiftArray
    .map((shift) => {
      if (!shift) return null;

      const matchedShiftValue = shiftOptions.find((option) => option.value === shift)?.value;
      if (matchedShiftValue) {
        return matchedShiftValue;
      }

      const normalized = shift.toLowerCase().replace(/[^a-z0-9]/g, '');

      const matchedShift = shiftOptions.find(
        (option) =>
          option.label.en.toLowerCase().replace(/[^a-z0-9]/g, '') === normalized ||
          option.label.nl.toLowerCase().replace(/[^a-z0-9]/g, '') === normalized,
      )?.value;

      return matchedShift || null;
    })
    .filter((shift): shift is string => shift !== null);
};

const normalizeEducationLevel = (level: string | undefined | null): string => {
  if (!level || isUnknownOrMissing(level)) return 'Not specified';

  const normalized = level.toLowerCase().replace(/[^a-z0-9]/g, '');

  const matchedLevel = EDUCATION_LEVELS.find(
    (option) =>
      normalized.includes(option.value.toLowerCase()) ||
      normalized.includes(option.label.en.toLowerCase()) ||
      normalized.includes(option.label.nl.toLowerCase()),
  )?.value;

  if (!matchedLevel) {
    const matchedEquivalent = EDUCATION_EQUIVALENTS.find((equivalent) => equivalent.old === level);
    if (matchedEquivalent) {
      return matchedEquivalent.modern || 'Not specified';
    }
  }

  return matchedLevel || 'Not specified';
};

const normalizeLanguageLevel = (level: string | undefined | null): string => {
  if (!level || isUnknownOrMissing(level)) return 'Not specified';

  const normalized = level.toLowerCase().replace(/[^a-z0-9]/g, '');

  const matchedLevel = LANGUAGE_LEVELS.find(
    (option) =>
      option.value.toLowerCase() === normalized ||
      option.label.en.toLowerCase() === normalized ||
      option.label.nl.toLowerCase() === normalized,
  )?.value;

  return matchedLevel || 'Not specified';
};

const normalizeDriverLicense = (license: string | string[] | undefined | null): string[] => {
  if (!license) return [];

  const licenseArray = Array.isArray(license) ? license : license.split(',').map((l) => l.trim());

  return licenseArray
    .map((lic) => {
      if (!lic) return null;

      const normalized = lic.toLowerCase().replace(/[^a-z0-9]/g, '');

      const matchedLicense = DRIVER_LICENSE_CATEGORIES.find(
        (option) =>
          option.value.toLowerCase() === normalized ||
          option.label.en.toLowerCase() === normalized ||
          option.label.nl.toLowerCase() === normalized,
      )?.value;

      return matchedLicense || null;
    })
    .filter((lic): lic is string => lic !== null);
};

export const mapJobImprovedGetDetailOutputToJobFormValues = (data: JobImprovedGetDetailOutput): JobFormValues => {
  let min_salary = '';
  let max_salary = '';
  if (!isUnknownOrMissing(data.job_details?.salary_range) && data.job_details?.salary_range) {
    const [min, max] = data.job_details.salary_range.split('-');
    min_salary = min?.trim() || '';
    max_salary = max?.trim() || '';
  }
  let min_working_hours = '';
  let max_working_hours = '';
  if (
    !isUnknownOrMissing(data.employment_requirements?.working_hours_range) &&
    data.employment_requirements?.working_hours_range
  ) {
    const [min, max] = data.employment_requirements.working_hours_range.split('-');
    min_working_hours = min?.trim() || '';
    max_working_hours = max?.trim() || '';
  }
  return {
    job_details: {
      job_title: isUnknownOrMissing(data.job_details?.job_title) ? '' : data.job_details?.job_title ?? '',
      employment_type: normalizeEmploymentType(data.job_details?.employment_type),
      min_salary,
      max_salary,
      salary_unit: isUnknownOrMissing(data.job_details?.salary_unit) ? '' : data.job_details?.salary_unit ?? '',
    },
    employment_requirements: {
      address: isUnknownOrMissing(data.employment_requirements?.address)
        ? ''
        : data.employment_requirements?.address ?? '',
      city: (() => {
        const raw = data.employment_requirements?.city;
        if (raw == null) return '';
        const s = String(raw).trim();
        return isUnknownOrMissing(s) ? '' : s;
      })(),
      latitude: parseNullableCoordinate(data.employment_requirements?.latitude),
      longitude: parseNullableCoordinate(data.employment_requirements?.longitude),
      place_id: (() => {
        const raw = data.employment_requirements?.place_id;
        if (raw == null) return null;
        const s = String(raw).trim();
        if (s === '' || isUnknownOrMissing(s)) return null;
        return s;
      })(),
      radius_km: isUnknownOrMissing(data.employment_requirements?.radius_km?.toString())
        ? 20
        : parseInt(data.employment_requirements?.radius_km?.toString() ?? '20', 10),
      min_working_hours,
      max_working_hours,
      shifts: isUnknownOrMissing(data.employment_requirements?.shifts)
        ? []
        : normalizeShifts(data.employment_requirements?.shifts),
      workdays: normalizeWorkdays(data.employment_requirements?.workdays),
    },
    position_overview: isUnknownOrMissing(data.position_overview?.description)
      ? ''
      : data.position_overview?.description ?? '',
    requirements: {
      minimum_education_level: isUnknownOrMissing(data.requirements?.minimum_education_level)
        ? ''
        : normalizeEducationLevel(data.requirements?.minimum_education_level),
      maximum_language_skills: !isUnknownOrMissing(data.requirements?.maximum_language_skills?.language)
        ? normalizeLanguage(data.requirements?.maximum_language_skills?.language)
        : [],
      languages_level: isUnknownOrMissing(data.requirements?.maximum_language_skills?.level)
        ? 'Not specified'
        : normalizeLanguageLevel(data.requirements?.maximum_language_skills?.level),
      certificates: isUnknownOrMissing(data.requirements?.certificates) ? '' : data.requirements?.certificates ?? '',
      drivers_license: normalizeDriverLicense(data.requirements?.drivers_license),
    },
    responsibilities: isUnknownOrMissing(
      Array.isArray(data.responsibilities?.key_responsibilities_tasks)
        ? data.responsibilities?.key_responsibilities_tasks?.join('\n')
        : typeof data.responsibilities?.key_responsibilities_tasks === 'string'
          ? data.responsibilities?.key_responsibilities_tasks
          : '',
    )
      ? ''
      : Array.isArray(data.responsibilities?.key_responsibilities_tasks)
        ? data.responsibilities?.key_responsibilities_tasks?.join('\n') ?? ''
        : typeof data.responsibilities?.key_responsibilities_tasks === 'string'
          ? data.responsibilities?.key_responsibilities_tasks
          : '',
    compensation_and_benefits: isUnknownOrMissing(
      Array.isArray(data.compensation_benefits?.notes)
        ? data.compensation_benefits?.notes?.join('\n')
        : typeof data.compensation_benefits?.notes === 'string'
          ? data.compensation_benefits?.notes
          : '',
    )
      ? ''
      : Array.isArray(data.compensation_benefits?.notes)
        ? data.compensation_benefits?.notes?.join('\n') ?? ''
        : typeof data.compensation_benefits?.notes === 'string'
          ? data.compensation_benefits?.notes
          : '',
  };
};

export const mapJobImprovedGeneratedByPreviewToJobFormValues = (data: JobImprovedGeneratedByPreview): JobFormValues => {
  return {
    compensation_and_benefits: data.compensation_and_benefits.notes,
    employment_requirements: {
      address: data.employment_requirements.address,
      city: '',
      latitude: null,
      longitude: null,
      place_id: null,
      radius_km: data.employment_requirements.radius_km,
      min_working_hours: data.employment_requirements.working_hours_range.min_working_hours?.toString() ?? '',
      max_working_hours: data.employment_requirements.working_hours_range.max_working_hours?.toString() ?? '',
      shifts: data.employment_requirements.shifts,
      workdays: data.employment_requirements.workdays,
    },
    job_details: {
      job_title: data.job_details.job_title,
      employment_type: data.job_details.employment_type,
      min_salary: data.job_details.salary_range.min_salary?.toString() ?? '',
      max_salary: data.job_details.salary_range.max_salary?.toString() ?? '',
      salary_unit: data.job_details.salary_unit,
    },
    position_overview: data.position_overview.description,
    requirements: {
      minimum_education_level: data.requirements.education_level,
      maximum_language_skills: data.requirements.language,
      languages_level: data.requirements.language_level,
      certificates: data.requirements.certificates,
      drivers_license: data.requirements.drivers_license,
    },
    responsibilities: data.responsibilities.key_responsibilities,
  };
};

export const mapJobImprovedGetDetailOutputToJobImprovedNestedInput = (
  data: JobImprovedGetDetailOutput,
): JobImprovedNestedInput => {
  const detailLatRaw = parseNullableCoordinate(data.employment_requirements.latitude);
  const detailLngRaw = parseNullableCoordinate(data.employment_requirements.longitude);
  const detailLat = detailLatRaw != null ? roundLatOrLngToSixDecimals(detailLatRaw) : null;
  const detailLng = detailLngRaw != null ? roundLatOrLngToSixDecimals(detailLngRaw) : null;
  const detailCityRaw = data.employment_requirements.city;
  const detailCityTrimmed = (() => {
    if (detailCityRaw == null) return '';
    const trimmed = String(detailCityRaw).trim();
    return isUnknownOrMissing(trimmed) ? '' : trimmed;
  })();
  const detailPlaceId =
    data.employment_requirements.place_id != null && String(data.employment_requirements.place_id).trim() !== ''
      ? String(data.employment_requirements.place_id).trim()
      : undefined;

  return {
    job_details: {
      job_title: data.job_details.job_title,
      application_deadline: data.job_details.application_deadline,
      employment_type: Array.isArray(data.job_details.employment_type)
        ? data.job_details.employment_type
        : data.job_details.employment_type?.split(',').map((t) => t.trim()) ?? [],
      salary_range: data.job_details.salary_range,
      salary_unit: data.job_details.salary_unit,
      working_hours_range: data.employment_requirements.working_hours_range,
      workdays: Array.isArray(data.employment_requirements.workdays)
        ? data.employment_requirements.workdays
        : data.employment_requirements.workdays?.split(',').map((d) => d.trim()) ?? [],
      shifts: Array.isArray(data.employment_requirements.shifts)
        ? data.employment_requirements.shifts
        : data.employment_requirements.shifts?.split(',').map((s) => s.trim()) ?? [],
    },
    location: {
      address: data.employment_requirements.address,
      radius_km: Number(data.employment_requirements.radius_km),
      ...(detailLat != null ? { latitude: detailLat } : {}),
      ...(detailLng != null ? { longitude: detailLng } : {}),
      city: detailCityTrimmed,
      ...(detailPlaceId !== undefined ? { place_id: detailPlaceId } : {}),
    },
    position_overview: {
      summary: data.position_overview.description,
    },
    requirements: {
      minimum_education_level: data.requirements.minimum_education_level,
      languages: Array.isArray(data.requirements.maximum_language_skills.language)
        ? data.requirements.maximum_language_skills.language
        : data.requirements.maximum_language_skills.language
          ? data.requirements.maximum_language_skills.language.split(',').map((l) => l.trim())
          : [],
      languages_level: data.requirements.maximum_language_skills.level,
      certificates: data.requirements.certificates,
      drivers_license: Array.isArray(data.requirements.drivers_license)
        ? data.requirements.drivers_license
        : data.requirements.drivers_license
          ? data.requirements.drivers_license.split(',').map((d) => d.trim())
          : [],
    },
    responsibilities: {
      key_responsibilities: Array.isArray(data.responsibilities.key_responsibilities_tasks)
        ? data.responsibilities.key_responsibilities_tasks.join('\n')
        : data.responsibilities.key_responsibilities_tasks,
    },
    compensation_and_benefits: {
      compensation_and_benefits: Array.isArray(data.compensation_benefits.notes)
        ? data.compensation_benefits.notes.join('\n')
        : data.compensation_benefits.notes,
    },
    summary: data.summary,
    quality_percent: data.quality_percent,
  };
};

const mapEmploymentRequirementsToLocationInput = (
  employment: JobFormValues['employment_requirements'] | undefined,
): LocationInput => {
  if (!employment) {
    return { address: '', radius_km: 0 };
  }

  const latitudeRaw = parseNullableCoordinate(employment.latitude);
  const longitudeRaw = parseNullableCoordinate(employment.longitude);
  const latitude = latitudeRaw != null ? roundLatOrLngToSixDecimals(latitudeRaw) : null;
  const longitude = longitudeRaw != null ? roundLatOrLngToSixDecimals(longitudeRaw) : null;
  const cityTrimmed = employment.city?.trim();
  const placeIdTrimmed =
    employment.place_id != null && String(employment.place_id).trim() !== ''
      ? String(employment.place_id).trim()
      : null;

  return {
    address: employment.address ?? '',
    radius_km: employment.radius_km ?? 0,
    ...(latitude != null ? { latitude } : {}),
    ...(longitude != null ? { longitude } : {}),
    city: cityTrimmed ?? '',
    ...(placeIdTrimmed ? { place_id: placeIdTrimmed } : {}),
  };
};

export const mapJobFormValuesToJobImprovedNestedInput = (data: JobFormValues): JobImprovedNestedInput => {
  const salary_range =
    data?.job_details?.min_salary || data?.job_details?.max_salary
      ? `${data.job_details.min_salary || ''}-${data.job_details.max_salary || ''}`
      : '';
  const working_hours_range =
    data?.employment_requirements?.min_working_hours || data?.employment_requirements?.max_working_hours
      ? `${data.employment_requirements.min_working_hours || ''}-${data.employment_requirements.max_working_hours || ''}`
      : '';
  return {
    job_details: {
      job_title: data?.job_details?.job_title ?? '',
      employment_type: data?.job_details?.employment_type ?? [],
      salary_range,
      salary_unit: data?.job_details?.salary_unit ?? '',
      working_hours_range,
      workdays: data?.employment_requirements?.workdays ?? [],
      shifts: data?.employment_requirements?.shifts ?? [],
    },
    location: mapEmploymentRequirementsToLocationInput(data?.employment_requirements),
    position_overview: {
      summary: data?.position_overview ?? '',
    },
    requirements: {
      minimum_education_level: data?.requirements?.minimum_education_level ?? '',
      languages: data?.requirements?.maximum_language_skills ?? [],
      languages_level: data?.requirements?.languages_level ?? '',
      certificates: data?.requirements?.certificates ?? '',
      drivers_license: data?.requirements?.drivers_license ?? [],
    },
    responsibilities: {
      key_responsibilities: data?.responsibilities ?? '',
    },
    compensation_and_benefits: {
      compensation_and_benefits: data?.compensation_and_benefits ?? '',
    },
  };
};

export const calculateFormCompletionPercentage = (formData: JobFormValues): number => {
  const normalizedFormData: JobFormValues = isLocationResolved(formData.employment_requirements)
    ? formData
    : {
        ...formData,
        employment_requirements: {
          ...formData.employment_requirements,
          address: '',
        },
      };

  const isFieldFilled = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') {
      return Object.values(value).some(isFieldFilled);
    }
    return true;
  };

  const countFields = (obj: any): { total: number; filled: number } => {
    let total = 0;
    let filled = 0;

    for (const value of Object.values(obj)) {
      if (typeof value === 'object' && value !== null) {
        const { total: subTotal, filled: subFilled } = countFields(value);
        total += subTotal;
        filled += subFilled;
      } else {
        total++;
        if (isFieldFilled(value)) {
          filled++;
        }
      }
    }

    return { total, filled };
  };

  const { total, filled } = countFields(normalizedFormData);
  return total === 0 ? 0 : Math.round((filled / total) * 100);
};
